import { createClient } from '@supabase/supabase-js';
import { validateEnvironment } from '../utils/env';
import { logger } from '../utils/logger';

/**
 * Supabase client configuration
 * Provides authentication and database access throughout the application
 */

// Wrap client creation in try-catch to prevent app crash
let supabase: ReturnType<typeof createClient>;

try {
  // Validate environment variables before creating client
  // This will throw if validation fails (fail-fast behavior)
  validateEnvironment();

  const url = import.meta.env.VITE_SUPABASE_URL as string;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

  if (!url || !key) {
    throw new Error('Supabase configuration is incomplete');
  }

  supabase = createClient(url, key, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false, // Manual handling in AuthGuard to avoid races
      storage: typeof window !== 'undefined' ? window.localStorage : undefined
    },
    global: {
      fetch: (url, options = {}) => {
        // Add 10 second timeout to prevent hanging requests
        return fetch(url, {
          ...options,
          signal: AbortSignal.timeout(10000) // 10 second timeout
        });
      }
    }
  });
} catch (error) {
  logger.error('Failed to create Supabase client', { error }, 'supabaseClient');
  
  // Create a mock client that throws on use (forces error boundary to catch)
  // This prevents the app from crashing immediately, allowing error boundary to handle it
  const createMockClient = () => {
    const mockError = new Error(
      'Supabase client not initialized. Check environment variables or database connection.'
    );
    
    return {
      auth: {
        getUser: () => Promise.resolve({ data: { user: null }, error: mockError }),
        getSession: () => Promise.resolve({ data: { session: null }, error: mockError }),
        signOut: () => Promise.resolve({ error: mockError }),
        onAuthStateChange: () => ({ data: { subscription: null }, error: mockError }),
      },
      from: () => {
        throw mockError;
      },
      rpc: () => {
        throw mockError;
      },
    } as any;
  };
  
  supabase = createMockClient();
  
  // Show user-visible error overlay (wait for DOM if needed)
  if (typeof window !== 'undefined') {
    const showError = () => {
      if (document.body) {
        const errorDiv = document.createElement('div');
        errorDiv.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: #dc2626;
          color: white;
          padding: 1rem;
          z-index: 99999;
          text-align: center;
          font-family: system-ui, -apple-system, sans-serif;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        `;
        errorDiv.innerHTML = `
          <strong style="font-size: 16px; display: block; margin-bottom: 0.5rem;">Configuration Error</strong>
          <div style="font-size: 14px;">
            Supabase client failed to initialize. Please check your environment variables or database connection.
          </div>
        `;
        if (document.body.firstChild) {
          document.body.insertBefore(errorDiv, document.body.firstChild);
        } else {
          document.body.appendChild(errorDiv);
        }
      } else {
        // DOM not ready yet, wait for it
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', showError);
        } else {
          // Fallback: use setTimeout to wait a bit
          setTimeout(showError, 100);
        }
      }
    };
    showError();
  }
}

export { supabase };